<mat-card class="customers-table-card">
  <mat-card-header>
    <mat-card-title>Ügyfelek listája</mat-card-title>
    <div class="header-actions">
      <app-add-new-button 
        text="Új ügyfél" 
        (clicked)="openNewCustomerDialog()">
      </app-add-new-button>
    </div>
  </mat-card-header>

  <mat-card-content>
    <!-- Loading indicator -->
    @if (isLoading) {
      <div class="loading-container">
        <mat-progress-spinner diameter="50" mode="indeterminate"></mat-progress-spinner>
        <p>Ügyfelek betöltése...</p>
      </div>
    }

    <!-- Error message -->
    @if (error && !isLoading) {
      <div class="error-container">
        <mat-icon color="warn">error</mat-icon>
        <p>{{ error }}</p>
        <button mat-button color="primary" (click)="loadCustomers()">
          <mat-icon>refresh</mat-icon>
          Újrapróbálás
        </button>
      </div>
    }

    <!-- Table -->
    @if (!isLoading && !error) {
      <!-- Desktop Table View -->
      <div class="desktop-view">
        <div class="table-container">
          <table mat-table [dataSource]="dataSource" class="customers-table">
            
            <!-- Céginformációk oszlop -->
            <ng-container matColumnDef="companyInfo">
              <th mat-header-cell *matHeaderCellDef>Céginformációk</th>
              <td mat-cell *matCellDef="let customer" class="company-info-cell">
                <!-- Cégek esetén -->
                @if (customer.type === 'company') {
                  <div class="company-name">
                    <mat-icon class="company-icon">business</mat-icon>
                    {{ customer.companyName }}
                  </div>
                  @if (customer.registrationNumber) {
                    <div class="registration-number">
                      Cégjegyzékszám: {{ customer.registrationNumber }}
                    </div>
                  }
                }
                
                <!-- Magánszemélyek esetén -->
                @if (customer.type === 'individual') {
                  <div class="company-name">
                    <mat-icon class="person-icon">person</mat-icon>
                    {{ customer.companyName }}
                  </div>
                }
                
                @if (customer.taxNumber) {
                  <div class="tax-number">Adószám: {{ customer.taxNumber }}</div>
                }
              </td>
            </ng-container>

            <!-- Tisztségviselők oszlop -->
            <ng-container matColumnDef="officials">
              <th mat-header-cell *matHeaderCellDef>Tisztségviselők</th>
              <td mat-cell *matCellDef="let customer" class="officials-cell">
                <div class="officials-chips-container">
                  @for (official of customer.officials; track $index + '_' + official) {
                    <app-call-center-chip 
                      [config]="{
                        text: official,
                        type: 'official',
                        validStatus: getOfficialValidStatus(official, customer),
                        showMenu: false
                      }">
                    </app-call-center-chip>
                  }
                </div>
                @if (customer.officials.length === 0) {
                  <div class="no-data">
                    Nincs adat
                  </div>
                }
              </td>
            </ng-container>

            <!-- Elérhetőségek oszlop -->
            <ng-container matColumnDef="contacts">
              <th mat-header-cell *matHeaderCellDef>Elérhetőségek</th>
              <td mat-cell *matCellDef="let customer" class="contacts-cell">
                <div class="contact-chips-container">
                  <!-- Email chipek -->
                  @if (customer.emails.length > 0) {
                    <div class="contact-section">
                      <div class="chips-group">
                        @for (email of customer.emails; track $index + '_' + email) {
                          <app-call-center-chip 
                            [config]="{
                              text: email,
                              type: 'email',
                              validStatus: getContactValidStatus(email, 'email', customer),
                              showMenu: false
                            }">
                          </app-call-center-chip>
                        }
                      </div>
                    </div>
                  }
                  
                  <!-- Telefon chipek -->
                  @if (customer.phoneNumbers.length > 0) {
                    <div class="contact-section">
                      <div class="chips-group">
                        @for (phone of customer.phoneNumbers; track $index + '_' + phone) {
                          <app-call-center-chip 
                            [config]="{
                              text: phone,
                              type: 'phone',
                              validStatus: getContactValidStatus(phone, 'phone', customer),
                              showMenu: false
                            }">
                          </app-call-center-chip>
                        }
                      </div>
                    </div>
                  }
                </div>
                
                @if (customer.emails.length === 0 && customer.phoneNumbers.length === 0) {
                  <div class="no-data">
                    Nincs adat
                  </div>
                }
              </td>
            </ng-container>

            <!-- Műveletek oszlop -->
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef>Műveletek</th>
              <td mat-cell *matCellDef="let customer">
                <button mat-icon-button (click)="onViewDetails(customer)" matTooltip="Adatlap">
                  <mat-icon>info</mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>
        </div>
      </div>

      <!-- Mobile Card View -->
      <div class="mobile-view">
        @for (customer of dataSource.data; track customer.type + '_' + customer.id + '_' + customer.companyName) {
          <div class="mobile-card">
            <div class="card-header">
              <div class="card-title">
                @if (customer.type === 'company') {
                  <mat-icon class="company-icon">business</mat-icon>
                } @else {
                  <mat-icon class="person-icon">person</mat-icon>
                }
                <strong>{{ customer.companyName }}</strong>
              </div>
              <button mat-icon-button (click)="onViewDetails(customer)" matTooltip="Adatlap" class="action-button">
                <mat-icon>info</mat-icon>
              </button>
            </div>

            <div class="card-content">
              <!-- Céginformációk -->
              <div class="card-section">
                <div class="section-label">Céginformációk</div>
                <div class="company-details">
                  @if (customer.registrationNumber) {
                    <div class="registration-number">Cégjegyzékszám: {{ customer.registrationNumber }}</div>
                  }
                  @if (customer.taxNumber) {
                    <div class="tax-number">Adószám: {{ customer.taxNumber }}</div>
                  }
                  @if (!customer.registrationNumber && !customer.taxNumber) {
                    <div class="no-data">Nincs további adat</div>
                  }
                </div>
              </div>

              <!-- Tisztségviselők -->
              @if (customer.officials.length > 0) {
                <div class="card-section">
                  <div class="section-label">Tisztségviselők</div>
                  <div class="officials-chips">
                    @for (official of customer.officials; track $index + '_' + official) {
                      <app-call-center-chip 
                        [config]="{
                          text: official,
                          type: 'official',
                          validStatus: getOfficialValidStatus(official, customer),
                          showMenu: false
                        }">
                      </app-call-center-chip>
                    }
                  </div>
                </div>
              }

              <!-- Elérhetőségek -->
              <div class="card-section">
                <div class="section-label">Elérhetőségek</div>
                <div class="contact-info">
                  <!-- Email -->
                  @if (customer.emails.length > 0) {
                    <div class="contact-group">
                      <div class="contact-type">Email</div>
                      <div class="email-chips">
                        @for (email of customer.emails; track $index + '_' + email) {
                          <app-call-center-chip 
                            [config]="{
                              text: email,
                              type: 'email',
                              validStatus: getContactValidStatus(email, 'email', customer),
                              showMenu: false
                            }">
                          </app-call-center-chip>
                        }
                      </div>
                    </div>
                  }
                  
                  <!-- Telefon -->
                  @if (customer.phoneNumbers.length > 0) {
                    <div class="contact-group">
                      <div class="contact-type">Telefon</div>
                      <div class="phone-chips">
                        @for (phone of customer.phoneNumbers; track $index + '_' + phone) {
                          <app-call-center-chip 
                            [config]="{
                              text: phone,
                              type: 'phone',
                              validStatus: getContactValidStatus(phone, 'phone', customer),
                              showMenu: false
                            }">
                          </app-call-center-chip>
                        }
                      </div>
                    </div>
                  }

                  @if (customer.emails.length === 0 && customer.phoneNumbers.length === 0) {
                    <div class="no-data">Nincs elérhetőség</div>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Paginátor -->
      <mat-paginator 
        #paginator
        [length]="totalCount"
        [pageSize]="pageSize" 
        [pageSizeOptions]="pageSizeOptions"
        [pageIndex]="pageIndex"
        (page)="onPageChange($event)"
        aria-label="Válassz oldalt"
        showFirstLastButtons>
      </mat-paginator>
    }
  </mat-card-content>
</mat-card>